import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import {
  commissionRequestsService,
  commissionRequestChip,
  type CommissionRequest,
  type CommissionRequestStatus,
} from '@services/commissionRequests.service';
import { usePermission } from '@hooks/usePermission';
import { cn, formatCurrency } from '@lib/utils';
import { formatTimestamp, humanizeCode } from '../finance/ledgerFormat';

type Filter = 'live' | CommissionRequestStatus;
type Decision = 'under_review' | 'approved' | 'rejected' | 'closed';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'live', label: 'Open & under review' },
  { value: 'open', label: 'Open' },
  { value: 'under_review', label: 'Under review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'closed', label: 'Closed' },
];

const DECISIONS: { value: Decision; label: string }[] = [
  { value: 'under_review', label: 'Mark under review' },
  { value: 'approved', label: 'Approve' },
  { value: 'rejected', label: 'Reject' },
  { value: 'closed', label: 'Close' },
];

function apiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; status?: unknown };
    if (e.status === 422) {
      return 'This request has already been resolved (approved, rejected or closed) and can no longer be changed. The list has been refreshed.';
    }
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

const pct = (v: number | null) => (v == null ? '—' : `${Number(v)}%`);

/**
 * COMMISSION REQUESTS — merchants asking for a lower commission rate.
 * Resolving here only records the decision and notifies the merchant. It does
 * NOT change the rate: apply it in Commissions (per-business config).
 */
export const CommissionRequestsPage = () => {
  const queryClient = useQueryClient();
  const { hasCheKitPermission } = usePermission();
  const canResolve = hasCheKitPermission('commissions.update');

  const [filter, setFilter] = useState<Filter>('live');
  const [active, setActive] = useState<CommissionRequest | null>(null);
  const [decision, setDecision] = useState<Decision>('under_review');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['commission-requests', filter],
    queryFn: () => commissionRequestsService.list(filter === 'live' ? undefined : filter),
  });

  // Live queue: oldest first (longest-waiting at the top). History: newest first.
  const rows = useMemo(() => {
    const list = [...(data ?? [])];
    const dir = filter === 'live' || filter === 'open' ? 1 : -1;
    return list.sort((a, b) => dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
  }, [data, filter]);

  const close = () => {
    setActive(null);
    setNote('');
    setError(null);
  };

  const resolveM = useMutation({
    mutationFn: ({ id, status, n }: { id: string; status: Decision; n: string }) =>
      commissionRequestsService.resolve(id, status, n),
    onSuccess: (_row, v) => {
      toast.success(
        v.status === 'approved'
          ? 'Approved and merchant notified. Now apply the new rate in Commissions.'
          : `Request ${humanizeCode(v.status).toLowerCase()}.`,
      );
      close();
      queryClient.invalidateQueries({ queryKey: ['commission-requests'] });
    },
    onError: (e) => {
      setError(apiError(e, 'Could not update the request.'));
      // 422 = someone else already resolved it; refresh so the stale row disappears.
      if ((e as { status?: number })?.status === 422) {
        queryClient.invalidateQueries({ queryKey: ['commission-requests'] });
      }
    },
  });

  return (
    <PageGuard permission="commissions.read">
      <div className="space-y-8">
        <div>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Commission requests
          </h1>
          <p className="mt-1 text-[14px] text-[var(--text-muted)]">
            Merchants asking for a lower commission rate. Resolving a request records the decision
            and notifies the merchant — it does not change their rate. Apply approved rates in{' '}
            <Link to="/commissions" className="underline">Commissions</Link>.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={cn(
                'rounded-full px-4 py-2 text-[13px] font-semibold transition-colors',
                filter === f.value
                  ? 'bg-[var(--text-primary)] text-[var(--bg-surface)]'
                  : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-base)]',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
          {isLoading && (
            <div className="flex items-center gap-2 p-12 text-[14px] text-[var(--text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading requests…
            </div>
          )}
          {!isLoading && rows.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {filter === 'live' ? 'No requests waiting — nothing to review.' : 'Nothing with that status.'}
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    <th className="px-6 py-4 font-semibold">Business</th>
                    <th className="px-6 py-4 font-semibold">Rate</th>
                    <th className="px-6 py-4 text-right font-semibold">Monthly volume</th>
                    <th className="px-6 py-4 font-semibold">Message</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Filed</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--border-subtle)] align-top">
                      <td className="px-6 py-4">
                        <Link to={`/businesses/${r.business_id}`} className="font-semibold text-[var(--text-primary)] hover:underline">
                          {r.business_name}
                        </Link>
                        <div className="text-[12px] text-[var(--text-muted)]">{r.category ? humanizeCode(r.category) : '—'}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 font-semibold text-[var(--text-primary)]">
                        {pct(r.current_rate_pct)} <span className="text-[var(--text-muted)]">→</span> {pct(r.requested_rate_pct)}
                      </td>
                      <td className="px-6 py-4 text-right text-[var(--text-secondary)]">
                        {r.monthly_volume_estimate == null ? '—' : formatCurrency(Number(r.monthly_volume_estimate))}
                      </td>
                      <td className="max-w-xs px-6 py-4 text-[13px] text-[var(--text-secondary)]">
                        {r.message || <span className="text-[var(--text-muted)]">—</span>}
                        {r.resolution_note && (
                          <div className="mt-1 text-[12px] text-[var(--text-muted)]">Staff note: {r.resolution_note}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn('rounded-full px-2.5 py-1 text-[12px] font-semibold', commissionRequestChip[r.status])}>
                          {humanizeCode(r.status)}
                        </span>
                        {r.reviewed_at && (
                          <div className="mt-1 text-[11px] text-[var(--text-muted)]">{formatTimestamp(r.reviewed_at)}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">{formatTimestamp(r.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        {canResolve && (r.status === 'open' || r.status === 'under_review') && (
                          <button
                            type="button"
                            onClick={() => {
                              setActive(r);
                              setDecision(r.status === 'open' ? 'under_review' : 'approved');
                              setNote('');
                              setError(null);
                            }}
                            className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)]"
                          >
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
          <div className="my-8 w-full max-w-lg rounded-3xl bg-[var(--bg-surface)] shadow-lift">
            <div className="border-b border-[var(--border-subtle)] px-6 py-5">
              <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {active.business_name}
              </h2>
              <p className="mt-1 text-[13px] text-[var(--text-muted)]">
                Asking to move from {pct(active.current_rate_pct)} to {pct(active.requested_rate_pct)}
              </p>
            </div>

            <div className="space-y-5 px-6 py-5">
              {active.message && (
                <div className="rounded-2xl bg-[var(--bg-base)] p-4 text-[13px] text-[var(--text-primary)]">
                  <p className="mb-1 font-semibold text-[var(--text-secondary)]">Merchant says</p>
                  {active.message}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">Decision</label>
                <select
                  value={decision}
                  onChange={(e) => setDecision(e.target.value as Decision)}
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                >
                  {DECISIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              {decision === 'approved' && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-warning-bg)] px-4 py-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-warning)]" strokeWidth={1.5} />
                  <p className="text-[13px] text-[var(--text-primary)]">
                    Approving records the decision and notifies the merchant — remember to apply the new
                    rate in the business&apos;s commission settings. It is not changed automatically.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Note <span className="text-[var(--text-muted)]">(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. Approved at 8% for 6 months based on volume"
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
                  <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-6 py-4">
              <button
                type="button"
                onClick={close}
                className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-base)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resolveM.isPending}
                onClick={() => resolveM.mutate({ id: active.id, status: decision, n: note.trim() })}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold text-[var(--bg-surface)] disabled:opacity-40"
              >
                {resolveM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </PageGuard>
  );
};
